/**
 * Delta WorkForce — official lockup.
 *
 * Source PNGs are pulled from deltawf.com brand assets and live in
 * `packages/web/public/`:
 *   - delta-workforce-logo.png        (slate text + slate triangle, for light backgrounds)
 *   - delta-workforce-logo-light.png  (white text + light triangle, for dark backgrounds)
 *
 * Both images are 933 × 202 (aspect ≈ 4.619). The triangular mark occupies
 * roughly the leftmost ~200 px, so `markOnly` crops to a near-square window.
 */

import { useEffect, useState } from 'react';

const SRC_DARK = '/dwf-logo-light.svg'; // dark text — for light backgrounds
const SRC_LIGHT = '/dwf-logo-dark.svg'; // white text — for dark backgrounds
const ASPECT = 232.91 / 50.23;
const MARK_WIDTH_RATIO = 200 / 202; // mark occupies ~200px of the 202px tall image

export type DeltaLogoVariant = 'dark' | 'light' | 'auto';

export interface DeltaWorkForceLogoProps {
  /** Total height in pixels. Default 40. */
  height?: number;
  /** Render only the triangular mark (no wordmark). */
  markOnly?: boolean;
  /**
   * Which colorway to use. `auto` (default) follows the document `data-theme`
   * attribute — dark themes get the white logo, light themes get the slate one.
   */
  variant?: DeltaLogoVariant;
  /** Optional subtitle below the wordmark (small caps). */
  subtitle?: string;
  /**
   * Deprecated. The official PNG cannot be recolored, so this prop is ignored.
   * Kept for backwards compatibility with earlier call sites.
   */
  textColor?: string;
}

function useResolvedVariant(variant: DeltaLogoVariant): 'dark' | 'light' {
  const [resolved, setResolved] = useState<'dark' | 'light'>(() => {
    if (variant !== 'auto') return variant;
    if (typeof document === 'undefined') return 'dark';
    const t = document.documentElement.getAttribute('data-theme');
    return t === 'light' ? 'dark' : 'light';
  });

  useEffect(() => {
    if (variant !== 'auto') {
      setResolved(variant);
      return;
    }
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const update = () => {
      const t = root.getAttribute('data-theme');
      setResolved(t === 'light' ? 'dark' : 'light');
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [variant]);

  return resolved;
}

export function DeltaWorkForceLogo({
  height = 40,
  markOnly = false,
  variant = 'auto',
  subtitle,
}: DeltaWorkForceLogoProps) {
  const resolved = useResolvedVariant(variant);
  const src = resolved === 'light' ? SRC_LIGHT : SRC_DARK;
  const fullWidth = Math.round(height * ASPECT);

  if (markOnly) {
    const cropWidth = Math.round(height * MARK_WIDTH_RATIO);
    return (
      <div
        style={{
          width: cropWidth,
          height,
          overflow: 'hidden',
          display: 'inline-block',
          flexShrink: 0,
        }}
        role="img"
        aria-label="Delta WorkForce"
      >
        <img
          src={src}
          alt=""
          width={fullWidth}
          height={height}
          style={{ display: 'block', objectFit: 'cover', objectPosition: 'left center' }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: subtitle ? Math.round(height * 0.12) : 0,
        lineHeight: 1,
      }}
    >
      <img
        src={src}
        alt="Delta WorkForce"
        width={fullWidth}
        height={height}
        style={{ display: 'block', height, width: 'auto' }}
      />
      {subtitle && (
        <span
          style={{
            fontFamily: "'DM Sans', system-ui, sans-serif",
            fontSize: Math.round(height * 0.24),
            color: 'var(--muted)',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginLeft: Math.round(height * MARK_WIDTH_RATIO + height * 0.18),
          }}
        >
          {subtitle}
        </span>
      )}
    </div>
  );
}
